import React,
{
    useCallback,
    useEffect,
    useRef,
    useState
}
from 'react';
import
{
    navigate
}
from './Router';
import masterVolume from './masterVolume';
import './ClickTheIdiot.css';

const READY_DURATION_MS = 1500;
const START_ANIMATION_DURATION_MS = 1000;
const STOP_SEQUENCE_DURATION_MS = 1850;
const GAME_DURATION_SECONDS = 16;
const GAME_DURATION_MS = GAME_DURATION_SECONDS * 1000;
const GAME_AUDIO_SRC = '/Audio/Games/onSite/ClickTheIdiot/15SecMusic.ogg';
const GAME_TIME_UP_AUDIO_SRC = '/Audio/Games/onSite/ClickTheIdiot/TimeUp.wav';
const GAME_BOOP_AUDIO_SRC = '/Audio/NoseBoop.ogg';
const GAME_BOOP_BASE_VOLUME = 0.5;
const TARGET_WIDTH_PX = 162;
const TARGET_HEIGHT_PX = 62;
const TARGET_MARGIN_PX = 28;
const AUTO_MOVE_MIN_MS = 220;
const AUTO_MOVE_MAX_MS = 420;
const CURSOR_AVOID_DISTANCE_PX = 150;
const CURSOR_PANIC_DISTANCE_PX = 88;
const CURSOR_REACT_COOLDOWN_MS = 110;
const RANDOM_ZIP_CHANCE = 0.12;
const PANIC_ZIP_CHANCE = 0.22;

function getMoveDurationMs(distance, mode)
{
    if (mode === 'center') return 0;

    if (mode === 'zip')
    {
        return Math.round(Math.max(150, Math.min(240, 140 + (distance * 0.18))));
    }

    if (mode === 'avoid')
    {
        return Math.round(Math.max(180, Math.min(280, 170 + (distance * 0.24))));
    }

    if (mode === 'resize')
    {
        return 120;
    }

    return Math.round(Math.max(210, Math.min(360, 200 + (distance * 0.3))));
}

function clamp01(value)
{
    return Math.max(0, Math.min(1, value));
}

function randomBetween(min, max)
{
    return min + (Math.random() * (max - min));
}

function getTimerColor(progress)
{
    const t = clamp01(progress);
    const red = Math.round(244 + ((255 - 244) * t));
    const green = Math.round(238 + ((92 - 238) * t));
    const blue = Math.round(228 + ((92 - 228) * t));
    return `rgb(${red}, ${green}, ${blue})`;
}

function readStageMetrics(stageElement)
{
    const stageRect = stageElement && typeof stageElement.getBoundingClientRect === 'function' ?
        stageElement.getBoundingClientRect() :
        null;
    const width = Math.max(320, stageRect && stageRect.width ? stageRect.width : window.innerWidth);
    const height = Math.max(320, stageRect && stageRect.height ? stageRect.height : window.innerHeight);
    const centerX = width / 2;
    const centerY = height / 2;
    const rawMinX = TARGET_MARGIN_PX + (TARGET_WIDTH_PX / 2);
    const rawMaxX = width - TARGET_MARGIN_PX - (TARGET_WIDTH_PX / 2);
    const rawMinY = TARGET_MARGIN_PX + (TARGET_HEIGHT_PX / 2);
    const rawMaxY = height - TARGET_MARGIN_PX - (TARGET_HEIGHT_PX / 2);
    const minX = rawMinX <= rawMaxX ? rawMinX : centerX;
    const maxX = rawMinX <= rawMaxX ? rawMaxX : centerX;
    const minY = rawMinY <= rawMaxY ? rawMinY : centerY;
    const maxY = rawMinY <= rawMaxY ? rawMaxY : centerY;

    return {
        width,
        height,
        centerX,
        centerY,
        minX,
        maxX,
        minY,
        maxY,
    };
}

function getCenteredTargetPosition(metrics)
{
    return {
        x: Math.max(metrics.minX, Math.min(metrics.maxX, metrics.centerX)),
        y: Math.max(metrics.minY, Math.min(metrics.maxY, metrics.centerY)),
    };
}

function clampTargetPosition(position, metrics)
{
    return {
        x: Math.max(metrics.minX, Math.min(metrics.maxX, position.x)),
        y: Math.max(metrics.minY, Math.min(metrics.maxY, position.y)),
    };
}

function getRandomTargetPosition(metrics)
{
    return {
        x: randomBetween(metrics.minX, metrics.maxX),
        y: randomBetween(metrics.minY, metrics.maxY),
    };
}

export default function ClickTheIdiot()
{
    const [phase, setPhase] = useState('idle');
    const [timeLeft, setTimeLeft] = useState(GAME_DURATION_SECONDS);
    const [remainingMs, setRemainingMs] = useState(GAME_DURATION_MS);
    const [finalClicks, setFinalClicks] = useState(0);
    const [targetPosition, setTargetPosition] = useState(
    {
        x: 0,
        y: 0
    });
    const [targetMotionMs, setTargetMotionMs] = useState(0);
    const gameAudioRef = useRef(null);
    const timeUpAudioRef = useRef(null);
    const clickBoopAudioRef = useRef(null);
    const roundFrameRef = useRef(null);
    const targetMoveTimeoutRef = useRef(null);
    const stopSequenceTimeoutRef = useRef(null);
    const clickCountRef = useRef(0);
    const stageRef = useRef(null);
    const targetPositionRef = useRef(
    {
        x: 0,
        y: 0
    });
    const cursorPositionRef = useRef(null);
    const lastCursorReactAtRef = useRef(0);

    const stopRoundFrame = useCallback(() =>
    {
        if (!roundFrameRef.current) return;
        try
        {
            cancelAnimationFrame(roundFrameRef.current);
        }
        catch (e)
        {}
        roundFrameRef.current = null;
    }, []);

    const clearTargetMoveTimeout = useCallback(() =>
    {
        if (!targetMoveTimeoutRef.current) return;
        try
        {
            clearTimeout(targetMoveTimeoutRef.current);
        }
        catch (e)
        {}
        targetMoveTimeoutRef.current = null;
    }, []);

    const clearStopSequenceTimeout = useCallback(() =>
    {
        if (!stopSequenceTimeoutRef.current) return;
        try
        {
            clearTimeout(stopSequenceTimeoutRef.current);
        }
        catch (e)
        {}
        stopSequenceTimeoutRef.current = null;
    }, []);

    const resetGameAudio = useCallback(() =>
    {
        const audio = gameAudioRef.current;
        if (!audio) return;
        try
        {
            audio.pause();
        }
        catch (e)
        {}
        try
        {
            audio.currentTime = 0;
        }
        catch (e)
        {}
    }, []);

    const playClickBoop = useCallback(() =>
    {
        const audio = clickBoopAudioRef.current;
        if (!audio) return;
        try
        {
            audio.pause();
        }
        catch (e)
        {}
        try
        {
            audio.currentTime = 0;
        }
        catch (e)
        {}
        try
        {
            audio.play().catch(() =>
            {});
        }
        catch (e)
        {}
    }, []);

    const playTimeUp = useCallback(() =>
    {
        const audio = timeUpAudioRef.current;
        if (!audio) return;
        try
        {
            audio.pause();
        }
        catch (e)
        {}
        try
        {
            audio.currentTime = 0;
        }
        catch (e)
        {}
        try
        {
            audio.play().catch(() =>
            {});
        }
        catch (e)
        {}
    }, []);

    const commitTargetPosition = useCallback((position, mode = 'roam') =>
    {
        const previous = targetPositionRef.current;
        const distance = Math.hypot(position.x - previous.x, position.y - previous.y);
        targetPositionRef.current = position;
        setTargetMotionMs(getMoveDurationMs(distance, mode));
        setTargetPosition(position);
    }, []);

    const moveTarget = useCallback((mode = 'roam') =>
    {
        const metrics = readStageMetrics(stageRef.current);
        const current = (targetPositionRef.current.x || targetPositionRef.current.y) ?
            targetPositionRef.current :
            getCenteredTargetPosition(metrics);

        if (mode === 'center')
        {
            commitTargetPosition(getCenteredTargetPosition(metrics), 'center');
            return;
        }

        if (mode === 'zip')
        {
            commitTargetPosition(getRandomTargetPosition(metrics), 'zip');
            return;
        }

        if (mode === 'avoid' && cursorPositionRef.current)
        {
            const cursor = cursorPositionRef.current;
            const deltaX = current.x - cursor.x;
            const deltaY = current.y - cursor.y;
            const distance = Math.hypot(deltaX, deltaY) || 1;
            const awayX = deltaX / distance;
            const awayY = deltaY / distance;
            const lateral = randomBetween(-50, 50);
            const escapeDistance = distance <CURSOR_PANIC_DISTANCE_PX ?
                randomBetween(150, 240) :
                randomBetween(90, 160);
            const next = clampTargetPosition(
            {
                x: current.x + (awayX * escapeDistance) - (awayY * lateral),
                y: current.y + (awayY * escapeDistance) + (awayX * lateral),
            }, metrics);
            commitTargetPosition(next, 'avoid');
            return;
        }

        const angle = Math.random() * Math.PI * 2;
        const step = randomBetween(48, 110);
        const next = clampTargetPosition(
        {
            x: current.x + (Math.cos(angle) * step),
            y: current.y + (Math.sin(angle) * step),
        }, metrics);
        commitTargetPosition(next, 'roam');
    }, [commitTargetPosition]);

    const queueNextAutoMove = useCallback(() =>
    {
        clearTargetMoveTimeout();
        targetMoveTimeoutRef.current = window.setTimeout(() =>
        {
            targetMoveTimeoutRef.current = null;
            if (phase !== 'started') return;

            const cursor = cursorPositionRef.current;
            const current = targetPositionRef.current;
            const cursorDistance = cursor ?
                Math.hypot(current.x - cursor.x, current.y - cursor.y) :
                Number.POSITIVE_INFINITY;

            if (cursorDistance <CURSOR_PANIC_DISTANCE_PX && Math.random() <PANIC_ZIP_CHANCE)
            {
                moveTarget('zip');
            }
            else if (cursorDistance <CURSOR_AVOID_DISTANCE_PX)
            {
                moveTarget('avoid');
            }
            else if (Math.random() <RANDOM_ZIP_CHANCE)
            {
                moveTarget('zip');
            }
            else
            {
                moveTarget('roam');
            }

            queueNextAutoMove();
        }, Math.round(randomBetween(AUTO_MOVE_MIN_MS, AUTO_MOVE_MAX_MS)));
    }, [clearTargetMoveTimeout, moveTarget, phase]);

    useEffect(() =>
    {
        const audio = new Audio(GAME_AUDIO_SRC);
        audio.preload = 'auto';
        audio._baseVolume = 0.8;
        try
        {
            masterVolume.applyToElement(audio);
        }
        catch (e)
        {}
        try
        {
            audio.load();
        }
        catch (e)
        {}
        gameAudioRef.current = audio;

        const timeUpAudio = new Audio(GAME_TIME_UP_AUDIO_SRC);
        timeUpAudio.preload = 'auto';
        timeUpAudio.loop = false;
        timeUpAudio._baseVolume = 0.8;
        try
        {
            masterVolume.applyToElement(timeUpAudio);
        }
        catch (e)
        {}
        try
        {
            timeUpAudio.load();
        }
        catch (e)
        {}
        timeUpAudioRef.current = timeUpAudio;

        const boopAudio = new Audio(GAME_BOOP_AUDIO_SRC);
        boopAudio.preload = 'auto';
        boopAudio.loop = false;
        boopAudio._baseVolume = GAME_BOOP_BASE_VOLUME;
        try
        {
            masterVolume.applyToElement(boopAudio);
        }
        catch (e)
        {}
        try
        {
            boopAudio.load();
        }
        catch (e)
        {}
        clickBoopAudioRef.current = boopAudio;

        return () =>
        {
            stopRoundFrame();
            clearTargetMoveTimeout();
            clearStopSequenceTimeout();
            try
            {
                audio.pause();
            }
            catch (e)
            {}
            try
            {
                audio.src = '';
            }
            catch (e)
            {}
            if (gameAudioRef.current === audio)
            {
                gameAudioRef.current = null;
            }

            try
            {
                timeUpAudio.pause();
            }
            catch (e)
            {}
            try
            {
                timeUpAudio.src = '';
            }
            catch (e)
            {}
            if (timeUpAudioRef.current === timeUpAudio)
            {
                timeUpAudioRef.current = null;
            }

            try
            {
                boopAudio.pause();
            }
            catch (e)
            {}
            try
            {
                boopAudio.src = '';
            }
            catch (e)
            {}
            if (clickBoopAudioRef.current === boopAudio)
            {
                clickBoopAudioRef.current = null;
            }
        };
    }, [clearStopSequenceTimeout, clearTargetMoveTimeout, stopRoundFrame]);

    useEffect(() =>
    {
        if (phase !== 'ready') return undefined;

        const readyTimer = window.setTimeout(() =>
        {
            setPhase('start');
        }, READY_DURATION_MS);

        return () => window.clearTimeout(readyTimer);
    }, [phase]);

    useEffect(() =>
    {
        if (phase !== 'start') return undefined;

        const startTimer = window.setTimeout(() =>
        {
            setPhase('started');
        }, START_ANIMATION_DURATION_MS);

        return () => window.clearTimeout(startTimer);
    }, [phase]);

    useEffect(() =>
    {
        if (phase !== 'stop') return undefined;

        clearStopSequenceTimeout();
        stopSequenceTimeoutRef.current = window.setTimeout(() =>
        {
            stopSequenceTimeoutRef.current = null;
            setPhase('results');
        }, STOP_SEQUENCE_DURATION_MS);

        return () => clearStopSequenceTimeout();
    }, [clearStopSequenceTimeout, phase]);

    useEffect(() =>
    {
        if (phase !== 'started') return undefined;

        stopRoundFrame();
        clearTargetMoveTimeout();
        clearStopSequenceTimeout();
        setTimeLeft(GAME_DURATION_SECONDS);
        setRemainingMs(GAME_DURATION_MS);
        setFinalClicks(0);
        clickCountRef.current = 0;
        cursorPositionRef.current = null;
        lastCursorReactAtRef.current = 0;
        commitTargetPosition(getCenteredTargetPosition(readStageMetrics(stageRef.current)), 'center');
        queueNextAutoMove();

        const audio = gameAudioRef.current;
        const roundStartedAt = performance.now();
        const roundEndsAt = roundStartedAt + GAME_DURATION_MS;

        if (audio)
        {
            try
            {
                audio.pause();
            }
            catch (e)
            {}
            try
            {
                audio.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                audio.play().catch(() =>
                {});
            }
            catch (e)
            {}
        }

        const syncRound = () =>
        {
            const nextRemainingMs = roundEndsAt - performance.now();

            if (nextRemainingMs <= 0)
            {
                setRemainingMs(0);
                setTimeLeft(0);
                setFinalClicks(clickCountRef.current);
                setTargetMotionMs(0);
                roundFrameRef.current = null;
                clearTargetMoveTimeout();
                playTimeUp();
                setPhase('stop');
                return;
            }

            const nextTimeLeft = Math.ceil(nextRemainingMs / 1000);
            setRemainingMs(nextRemainingMs);
            setTimeLeft((current) => (current === nextTimeLeft ? current : nextTimeLeft));
            roundFrameRef.current = requestAnimationFrame(syncRound);
        };

        syncRound();

        return () =>
        {
            stopRoundFrame();
            clearTargetMoveTimeout();
        };
    }, [clearStopSequenceTimeout, clearTargetMoveTimeout, commitTargetPosition, phase, playTimeUp, queueNextAutoMove, stopRoundFrame]);

    useEffect(() =>
    {
        if (phase !== 'started') return undefined;

        const handleResize = () =>
        {
            const metrics = readStageMetrics(stageRef.current);
            const current = targetPositionRef.current;

            if (!current.x && !current.y)
            {
                commitTargetPosition(getCenteredTargetPosition(metrics), 'center');
                return;
            }

            commitTargetPosition(clampTargetPosition(current, metrics), 'resize');
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [commitTargetPosition, phase]);

    const handlePlay = () =>
    {
        if (phase !== 'idle') return;
        resetGameAudio();
        setPhase('ready');
    };

    const handlePlayAgain = () =>
    {
        if (phase !== 'results') return;
        resetGameAudio();
        setPhase('ready');
    };

    const handleTargetClick = () =>
    {
        if (phase !== 'started') return;
        clickCountRef.current += 1;
        playClickBoop();

        if (Math.random() < 0.18)
        {
            moveTarget('zip');
        }
        else
        {
            moveTarget('roam');
        }

        queueNextAutoMove();
    };

    const handleStagePointerMove = (event) =>
    {
        if (phase !== 'started') return;
        const stageRect = stageRef.current && typeof stageRef.current.getBoundingClientRect === 'function' ?
            stageRef.current.getBoundingClientRect() :
            null;
        if (!stageRect) return;

        const cursor = {
            x: event.clientX - stageRect.left,
            y: event.clientY - stageRect.top,
        };
        cursorPositionRef.current = cursor;

        const current = targetPositionRef.current;
        const cursorDistance = Math.hypot(current.x - cursor.x, current.y - cursor.y);
        if (cursorDistance >= CURSOR_AVOID_DISTANCE_PX) return;

        const now = performance.now();
        if ((now - lastCursorReactAtRef.current) <CURSOR_REACT_COOLDOWN_MS) return;
        lastCursorReactAtRef.current = now;

        if (cursorDistance <CURSOR_PANIC_DISTANCE_PX && Math.random() <PANIC_ZIP_CHANCE)
        {
            moveTarget('zip');
        }
        else
        {
            moveTarget('avoid');
        }

        queueNextAutoMove();
    };

    const timerDangerProgress = remainingMs >= 5000 ?
        0 :
        clamp01((5000 - remainingMs) / 5000);

    return ( <div className = "click-the-idiot-page" >
        <div className = {
            phase === 'idle' ? 'click-the-idiot-shell' : 'click-the-idiot-shell click-the-idiot-shell--hidden'
        } >
        <div className = "click-the-idiot-eyebrow" > Mini Game </div> <h1 className = "click-the-idiot-title" > Click The Idiot </h1> <div className = "click-the-idiot-actions" >
        <button type = "button"
        className = "click-the-idiot-button click-the-idiot-button--primary"
        onClick = {
            handlePlay
        } >
        Play </button> <button type = "button"
        className = "click-the-idiot-button"
        onClick = {
            () => navigate('/Projects')
        } >
        Exit to Projects </button> </div> </div>

        {
            phase !== 'idle' ? ( <div className = "click-the-idiot-sequence"
                aria-live = "polite" >
                {
                    phase === 'ready' ? ( <div className = "click-the-idiot-sequenceText click-the-idiot-sequenceText--ready" > Ready </div>
                    ) : null
                }

                {
                    phase === 'start' ? ( <div className = "click-the-idiot-sequenceText click-the-idiot-sequenceText--start" > Start </div>
                    ) : null
                }

                {
                    phase === 'stop' ? ( <div className = "click-the-idiot-sequenceText click-the-idiot-sequenceText--stop" > STOP </div>
                    ) : null
                }

                {
                    phase === 'started' || phase === 'stop' ? ( <div className = {
                            phase === 'stop' ? 'click-the-idiot-gameStage click-the-idiot-gameStage--ending' : 'click-the-idiot-gameStage'
                        } >
                        <div className = "click-the-idiot-stageHud" >
                        <div className = "click-the-idiot-hud" >
                        <div className = "click-the-idiot-hudCard" >
                        <div className = "click-the-idiot-hudLabel" > Time Left </div> <div className = "click-the-idiot-hudValue"
                        style = {
                            {
                                color: getTimerColor(timerDangerProgress)
                            }
                        } >
                        {
                            timeLeft
                        } </div> </div> </div> </div>

                        <div className = "click-the-idiot-stageArena" >
                        <div ref = {
                            stageRef
                        }
                        className = "click-the-idiot-playStage"
                        onPointerMove = {
                            handleStagePointerMove
                        } >
                        <div className = "click-the-idiot-stageCenter"
                        aria-hidden = "true" />
                        <button type = "button"
                        className = "click-the-idiot-targetButton"
                        disabled = {
                            phase !== 'started'
                        }
                        onClick = {
                            handleTargetClick
                        }
                        style = {
                            {
                                left: `${targetPosition.x}px`,
                                top: `${targetPosition.y}px`,
                                '--target-move-ms': `${targetMotionMs}ms`,
                            }
                        } >
                        Click Me </button> </div> </div> </div>
                    ) : null
                }

                {
                    phase === 'results' ? ( <div className = "click-the-idiot-results click-the-idiot-results--entering" >
                        <div className = "click-the-idiot-resultsLabel" > Game Over </div> <div className = "click-the-idiot-resultsValue" >
                        {
                            finalClicks
                        } </div> <div className = "click-the-idiot-resultsText" > You clicked the button
                        {
                            finalClicks
                        }
                        time
                        {
                            finalClicks === 1 ? '' : 's'
                        }. </div> <div className = "click-the-idiot-resultsActions" >
                        <button type = "button"
                        className = "click-the-idiot-button click-the-idiot-button--primary"
                        onClick = {
                            handlePlayAgain
                        } >
                        Play Again </button> <button type = "button"
                        className = "click-the-idiot-button"
                        onClick = {
                            () => navigate('/Projects')
                        } >
                        Exit to Projects </button> </div> </div>
                    ) : null
                } </div>
            ) : null
        } </div>
    );
}
